// eslint-disable-next-line import/no-extraneous-dependencies
import { usePostHog } from 'posthog-js/react';
import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { POSTHOG_SURVEYS } from '@/constants/analytics/posthog-surveys';
import { cn } from '@/lib/utils';

const SURVEY = POSTHOG_SURVEYS.TASK_CSAT;
const QUESTION = SURVEY.questions[0];

// Emoji ratings from dissatisfied to satisfied
const EMOJI_RATINGS = [
  { value: 1, emoji: '😠', label: 'Very dissatisfied' },
  { value: 2, emoji: '😕', label: 'Dissatisfied' },
  { value: 3, emoji: '😐', label: 'Neutral' },
  { value: 4, emoji: '🙂', label: 'Satisfied' },
  { value: 5, emoji: '😍', label: 'Very satisfied' },
];

interface TaskCSATSurveyProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const TaskCSATSurvey: React.FC<TaskCSATSurveyProps> = ({
  open,
  onOpenChange,
}) => {
  const posthog = usePostHog();

  // Track survey shown when dialog opens
  React.useEffect(() => {
    if (open) {
      posthog.capture('survey shown', {
        $survey_id: SURVEY.id,
      });
    }
  }, [open, posthog]);

  const handleRatingClick = (rating: number) => {
    // Capture as custom event (always captured, no deduplication)
    posthog.capture('task_csat_response', {
      survey_id: SURVEY.id,
      question_id: QUESTION.id,
      question: QUESTION.question,
      rating,
      rating_label: EMOJI_RATINGS.find((r) => r.value === rating)?.label,
    });

    // Also capture as survey sent for PostHog survey analytics
    posthog.capture('survey sent', {
      $survey_id: SURVEY.id,
      $survey_questions: [
        {
          id: QUESTION.id,
          question: QUESTION.question,
        },
      ],
      [`$survey_response_${QUESTION.id}`]: rating.toString(),
    });

    // Dismiss modal immediately after rating
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" hideCloseButton>
        <DialogHeader>
          <DialogTitle className="text-center text-lg font-medium">
            {QUESTION.question}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Rate your experience from 1 to 5
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          {/* Emoji rating buttons */}
          <div className="flex justify-center gap-2">
            {EMOJI_RATINGS.map(({ value, emoji, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => handleRatingClick(value)}
                className={cn(
                  'flex flex-col items-center gap-1 p-3 rounded-lg transition-all',
                  'hover:bg-muted hover:scale-110',
                  'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                )}
                aria-label={label}
              >
                <span className="text-3xl">{emoji}</span>
              </button>
            ))}
          </div>

          {/* Labels */}
          <div className="flex justify-between text-xs text-muted-foreground px-2">
            <span>{QUESTION.lowerBoundLabel}</span>
            <span>{QUESTION.upperBoundLabel}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
